import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, Pencil, Play, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { CardList } from "@/components/flashcards/CardList"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { DeckEditor } from "@/components/flashcards/DeckEditor"
import { DeckFormDialog } from "@/components/flashcards/DeckFormDialog"
import { ReviewToggle } from "@/components/flashcards/DeckList"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { ContentAreaGroup, DeckDetail } from "@/types/flashcards"

export default function DeckShow({
  deck,
  content_area_groups,
}: {
  deck: DeckDetail
  content_area_groups: ContentAreaGroup[]
}) {
  const intl = useIntl()
  const format = useFormat()
  const [editing, setEditing] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const basePath = `/decks/${deck.id}`
  const summary = intl.formatMessage({ id: "flashcards.deck_meta" }, { title: deck.title, cards: format.cardCount(deck.card_count) })

  return (
    <>
      <Head title={deck.title}>
        <meta name="description" content={deck.description || summary} />
        <meta property="og:title" content={deck.title} />
        <meta property="og:description" content={deck.description || summary} />
      </Head>
      <AppShell>
        <Link href="/flashcards" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.back" })}
        </Link>
        <PageHeader
          title={deck.title}
          description={
            <>
              {deck.official && (
                <Badge tone="accent" className="mr-2">
                  {intl.formatMessage({ id: "flashcards.official" })}
                </Badge>
              )}
              {format.cardCount(deck.card_count)}
              {deck.in_review && deck.due ? ` · ${intl.formatMessage({ id: "flashcards.due_today" }, { count: deck.due })}` : ""}
              {deck.description ? ` · ${deck.description}` : ""}
              {!deck.official && deck.source && (
                <>
                  {` · ${intl.formatMessage({ id: "flashcards.made_from" })} `}
                  <Link href={`/sources/${deck.source.id}`}>{deck.source.title}</Link>
                </>
              )}
            </>
          }
          actions={
            <>
              <ReviewToggle deck={deck} />
              {!deck.official && (
                <>
                  <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "flashcards.edit_deck" })} onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "flashcards.delete_deck" })} onClick={() => setDeleting(true)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
              {deck.card_count > 0 && (
                <Button asChild>
                  <Link href={`${basePath}/study`}>
                    <Play className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.study" })}
                  </Link>
                </Button>
              )}
            </>
          }
        />

        {deck.official ? (
          <section className="mt-8">
            <h2>{intl.formatMessage({ id: "flashcards.cards" })}</h2>
            <CardList cards={deck.cards} />
          </section>
        ) : (
          <DeckEditor deck={deck} basePath={basePath} contentAreaGroups={content_area_groups} />
        )}

        {!deck.official && (
          <>
            <DeckFormDialog
              open={editing}
              onOpenChange={setEditing}
              action={basePath}
              method="patch"
              title={intl.formatMessage({ id: "flashcards.edit_deck_title" })}
              submitLabel={intl.formatMessage({ id: "flashcards.save" })}
              initial={{ title: deck.title, description: deck.description }}
            />
            <ConfirmDialog
              open={deleting}
              onOpenChange={setDeleting}
              title={intl.formatMessage({ id: "flashcards.delete_deck_title" })}
              description={intl.formatMessage({ id: "flashcards.delete_deck_body" }, { title: deck.title, cards: format.cardCount(deck.card_count) })}
              confirmLabel={intl.formatMessage({ id: "flashcards.delete_deck" })}
              onConfirm={() => router.delete(basePath)}
            />
          </>
        )}
      </AppShell>
    </>
  )
}
